/**
 * Templates: starting projects (house, apartment, workshop) and pieces to insert into an
 * existing project (EV charger, PV inverter, heat pump on the board; impulse-relay lighting
 * and roller-shutter control sheets).
 */
import {
  applyTransaction,
  createProject,
  newId,
  sheetsInOrder,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type WireEnd,
  tr,
} from '@elec/domain';
import { analyse } from '@elec/analysis';
import {
  appendCircuitOps,
  appendGroupOps,
  generateBoardOps,
  type BoardSpec,
  type CircuitSpec,
} from '@elec/editor';

type L = { fr: string; en: string; nl: string };
const xvb = (s: number) => ({ type: 'XVB', cores: 3, sectionMm2: s });
const sockets = (n: number, props: Record<string, PropValue> = {}) =>
  Array.from({ length: n }, () => ({ symbolId: 'iec.socket', props }));
const lights = (n: number) => Array.from({ length: n }, () => ({ symbolId: 'iec.light' }));
const c = (
  label: string,
  ratingA: number,
  curve: 'B' | 'C',
  points: CircuitSpec['points'],
  section: number,
): CircuitSpec => ({
  label,
  device: { symbolId: 'iec.breaker', props: { ratingA, curve } },
  points,
  cable: xvb(section),
});

// ---------- project templates ----------

export interface ProjectTemplate {
  id: string;
  name: L;
  description: L;
  create(locale: 'fr' | 'en' | 'nl'): Project;
}

function boardProject(locale: 'fr' | 'en' | 'nl', title: string, board: BoardSpec, withPlan = true): Project {
  const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
  let p = createProject({ title, locale });
  p = applyTransaction(p, {
    label: '',
    ops: [{ op: 'meta', patch: { supplyVoltage: String(board.supply?.voltage ?? '230 V') } }],
  }).project;
  const sl = Object.keys(p.sheets)[0]!;
  const ops: Op[] = [
    {
      op: 'update',
      coll: 'sheets',
      id: sl,
      patch: { name: T('Tableau principal', 'Main board', 'Hoofdbord') },
    },
  ];
  if (withPlan)
    ops.push({
      op: 'add',
      coll: 'sheets',
      value: {
        id: newId(),
        name: T('Plan de position', 'Position plan', 'Situatieschema'),
        kind: 'position-plan',
        paper: 'A3',
        orientation: 'landscape',
        order: 1,
        planScale: 50,
      },
    });
  p = applyTransaction(p, { label: '', ops }).project;
  p = applyTransaction(p, { label: '', ops: generateBoardOps(p, sl, board) }).project;
  return p;
}

const main = { ratingA: 40, poles: 2 };
const rcd300 = { ratingA: 40, sensitivityMa: 300, rcdType: 'A', poles: 2 };
const rcd30 = { ratingA: 40, sensitivityMa: 30, rcdType: 'A', poles: 2 };

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'house',
    name: { fr: 'Maison unifamiliale', en: 'Single-family house', nl: 'Eengezinswoning' },
    description: {
      fr: '8 circuits : prises, éclairage, cuisson, chauffe-eau, pièces humides sous 30 mA.',
      en: '8 circuits: sockets, lighting, hob, water heater, wet rooms under 30 mA.',
      nl: '8 kringen: stopcontacten, verlichting, kookplaat, boiler, vochtige ruimtes onder 30 mA.',
    },
    create: (locale) => {
      const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
      return boardProject(locale, T('Maison', 'House', 'Woning'), {
        supply: { voltage: '230 V', ratingA: 40 },
        main,
        mainRcd: rcd300,
        groups: [
          {
            circuits: [
              c(
                T('Prises séjour', 'Living room sockets', 'Stopcontacten woonkamer'),
                20,
                'C',
                sockets(6),
                2.5,
              ),
              c(
                T('Prises chambres', 'Bedroom sockets', 'Stopcontacten slaapkamers'),
                20,
                'C',
                sockets(6),
                2.5,
              ),
              c(T('Éclairage', 'Lighting', 'Verlichting'), 16, 'B', lights(8), 1.5),
              c(T('Taque de cuisson', 'Hob', 'Kookplaat'), 32, 'C', [{ symbolId: 'iec.cooker' }], 6),
              c(T('Chauffe-eau', 'Water heater', 'Boiler'), 20, 'C', [{ symbolId: 'iec.water-heater' }], 2.5),
            ],
          },
          {
            rcd: rcd30,
            circuits: [
              c(T('Salle de bain', 'Bathroom', 'Badkamer'), 20, 'C', sockets(2, { ip: 'IP44' }), 2.5),
              c(
                T('Lave-linge', 'Washing machine', 'Wasmachine'),
                20,
                'C',
                [{ symbolId: 'iec.washing-machine' }],
                2.5,
              ),
              c(
                T('Lave-vaisselle', 'Dishwasher', 'Vaatwasser'),
                20,
                'C',
                [{ symbolId: 'iec.dishwasher' }],
                2.5,
              ),
            ],
          },
        ],
      });
    },
  },
  {
    id: 'apartment',
    name: { fr: 'Appartement', en: 'Apartment', nl: 'Appartement' },
    description: {
      fr: '6 circuits, compteur en commun, pièces humides sous 30 mA.',
      en: '6 circuits, shared meter, wet rooms under 30 mA.',
      nl: '6 kringen, gemeenschappelijke meter, vochtige ruimtes onder 30 mA.',
    },
    create: (locale) => {
      const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
      return boardProject(locale, T('Appartement', 'Apartment', 'Appartement'), {
        supply: { voltage: '230 V', ratingA: 40 },
        meter: false,
        main,
        mainRcd: rcd300,
        groups: [
          {
            circuits: [
              c(T('Prises', 'Sockets', 'Stopcontacten'), 20, 'C', sockets(7), 2.5),
              c(T('Éclairage', 'Lighting', 'Verlichting'), 16, 'B', lights(7), 1.5),
              c(T('Cuisine', 'Kitchen', 'Keuken'), 32, 'C', [{ symbolId: 'iec.cooker' }], 6),
            ],
          },
          {
            rcd: rcd30,
            circuits: [
              c(T('Salle de douche', 'Shower room', 'Douche'), 20, 'C', sockets(1, { ip: 'IP44' }), 2.5),
              c(
                T('Lave-linge', 'Washing machine', 'Wasmachine'),
                20,
                'C',
                [{ symbolId: 'iec.washing-machine' }],
                2.5,
              ),
              c(
                T('Lave-vaisselle', 'Dishwasher', 'Vaatwasser'),
                20,
                'C',
                [{ symbolId: 'iec.dishwasher' }],
                2.5,
              ),
            ],
          },
        ],
      });
    },
  },
  {
    id: 'workshop',
    name: { fr: 'Garage / atelier', en: 'Garage / workshop', nl: 'Garage / werkplaats' },
    description: {
      fr: 'Tableau divisionnaire : prises, éclairage, prise triphasée.',
      en: 'Sub-board: sockets, lighting, three-phase socket.',
      nl: 'Onderverdeelbord: stopcontacten, verlichting, driefasig stopcontact.',
    },
    create: (locale) => {
      const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
      return boardProject(
        locale,
        T('Garage / atelier', 'Garage / workshop', 'Garage / werkplaats'),
        {
          supply: { voltage: '3N×400 V', ratingA: 25 },
          meter: false,
          main: { ratingA: 40, poles: 4 },
          groups: [
            {
              rcd: { ...rcd30, poles: 4 },
              circuits: [
                c(
                  T('Prises atelier', 'Workshop sockets', 'Stopcontacten werkplaats'),
                  20,
                  'C',
                  sockets(4, { ip: 'IP44' }),
                  2.5,
                ),
                c(
                  T('Éclairage', 'Lighting', 'Verlichting'),
                  16,
                  'B',
                  [
                    { symbolId: 'iec.light-linear' },
                    { symbolId: 'iec.light-linear' },
                    { symbolId: 'iec.light-linear' },
                  ],
                  1.5,
                ),
                {
                  label: T('Prise triphasée', 'Three-phase socket', 'Driefasig stopcontact'),
                  device: { symbolId: 'iec.breaker', props: { ratingA: 16, curve: 'C', poles: 4 } },
                  points: [{ symbolId: 'iec.socket-3p', props: { ratingA: 16 } }],
                  cable: { type: 'XVB', cores: 5, sectionMm2: 2.5 },
                },
              ],
            },
          ],
        },
        false,
      );
    },
  },
];

// ---------- insert templates ----------

export interface InsertTemplate {
  id: string;
  name: L;
  description: L;
  kind: 'board' | 'control';
  /** Ops adding the piece to the project; `sheetId` is the target single-line sheet for board pieces. */
  ops(project: Project, sheetId: Id | undefined): { ops: Op[]; sheetId: Id };
}

function firstSingleLine(project: Project, sheetId: Id | undefined): Id | undefined {
  if (sheetId && project.sheets[sheetId]?.kind === 'single-line') return sheetId;
  return sheetsInOrder(project).find((s) => s.kind === 'single-line')?.id;
}

function originRcd(project: Project, sheetId: Id): Id | undefined {
  return analyse(project)
    .circuits.find((x) => x.sheet.id === sheetId)
    ?.rcds.at(-1)?.id;
}

/** Small drawing helper for control sheets. */
class Draw {
  ops: Op[] = [];
  readonly sheetId: Id;
  constructor(sheetId: Id) {
    this.sheetId = sheetId;
  }
  el(symbolId: string, x: number, y: number, tag: string, props: Record<string, PropValue> = {}) {
    const id = newId();
    this.ops.push({
      op: 'add',
      coll: 'elements',
      value: { id, sheetId: this.sheetId, symbolId, x, y, rotation: 0, mirrored: false, tag, props },
    });
    return id;
  }
  wire(from: WireEnd, to: WireEnd, via: Array<{ x: number; y: number }> = [], label?: string) {
    this.ops.push({
      op: 'add',
      coll: 'wires',
      value: { id: newId(), sheetId: this.sheetId, from, to, via, ...(label ? { label } : {}) },
    });
  }
  text(x: number, y: number, text: string, size = 3) {
    this.el('annot.text', x, y, '', { text, size });
  }
}
const P = (elementId: Id, portId: string): WireEnd => ({ kind: 'port', elementId, portId });
const Q = (x: number, y: number): WireEnd => ({ kind: 'point', x, y });

function controlSheet(project: Project, name: string): { sheetOp: Op; sheetId: Id } {
  const id = newId();
  const order = Math.max(...Object.values(project.sheets).map((s) => s.order)) + 1;
  return {
    sheetId: id,
    sheetOp: {
      op: 'add',
      coll: 'sheets',
      value: { id, name, kind: 'control', paper: 'A4', orientation: 'landscape', order },
    },
  };
}

/** Next free tag with a prefix across the project (K3, S4…). */
function freeTags(project: Project, prefix: string, n: number, from = 1): string[] {
  const used = new Set(Object.values(project.elements).map((e) => e.tag));
  const out: string[] = [];
  for (let i = from; out.length < n; i++) if (!used.has(`${prefix}${i}`)) out.push(`${prefix}${i}`);
  return out;
}

export const INSERT_TEMPLATES: InsertTemplate[] = [
  {
    id: 'ev-charger',
    kind: 'board',
    name: { fr: 'Borne de recharge VE', en: 'EV charger', nl: 'Laadpaal' },
    description: {
      fr: 'Circuit dédié 32 A, 6 mm², sous un différentiel 30 mA dédié (type à vérifier selon la borne).',
      en: 'Dedicated 32 A circuit, 6 mm², behind its own 30 mA RCD (type depends on the charger).',
      nl: 'Eigen kring van 32 A, 6 mm², achter een eigen differentieel van 30 mA (type afhankelijk van de laadpaal).',
    },
    ops: (project, sheetId) => {
      const s = firstSingleLine(project, sheetId);
      if (!s) return { ops: [], sheetId: '' };
      const T = (f: string, e: string, n: string) => tr(project.meta.locale, f, e, n);
      return {
        sheetId: s,
        ops: appendGroupOps(project, s, { ratingA: 40, sensitivityMa: 30, rcdType: 'A', poles: 2 }, [
          {
            label: T('Borne de recharge VE', 'EV charger', 'Laadpaal'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 32, curve: 'C' } },
            // A "7.4 kW" charger draws 32 A at 230 V: 7.36 kW, so Ib stays within the 32 A breaker.
            points: [{ symbolId: 'iec.ev-charger', props: { powerKw: 7.36 } }],
            cable: xvb(6),
          },
        ]),
      };
    },
  },
  {
    id: 'pv',
    kind: 'board',
    name: { fr: 'Onduleur photovoltaïque', en: 'PV inverter', nl: 'PV-omvormer' },
    description: {
      fr: 'Circuit dédié 20 A, 4 mm², avec son propre différentiel (selon l’onduleur).',
      en: 'Dedicated 20 A circuit, 4 mm², with its own RCD (as required by the inverter).',
      nl: 'Eigen kring van 20 A, 4 mm², met een eigen differentieel (zoals de omvormer vereist).',
    },
    ops: (project, sheetId) => {
      const s = firstSingleLine(project, sheetId);
      if (!s) return { ops: [], sheetId: '' };
      const T = (f: string, e: string, n: string) => tr(project.meta.locale, f, e, n);
      return {
        sheetId: s,
        ops: appendGroupOps(project, s, { ratingA: 40, sensitivityMa: 30, rcdType: 'A', poles: 2 }, [
          {
            label: T('Onduleur PV', 'PV inverter', 'PV-omvormer'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.inverter', props: { powerKw: 4 } }],
            cable: xvb(4),
          },
        ]),
      };
    },
  },
  {
    id: 'heat-pump',
    kind: 'board',
    name: { fr: 'Pompe à chaleur', en: 'Heat pump', nl: 'Warmtepomp' },
    description: {
      fr: 'Circuit dédié 20 A courbe C, 4 mm², sous le différentiel général.',
      en: 'Dedicated 20 A curve C circuit, 4 mm², under the main RCD.',
      nl: 'Eigen kring van 20 A curve C, 4 mm², onder het hoofddifferentieel.',
    },
    ops: (project, sheetId) => {
      const s = firstSingleLine(project, sheetId);
      if (!s) return { ops: [], sheetId: '' };
      const T = (f: string, e: string, n: string) => tr(project.meta.locale, f, e, n);
      return {
        sheetId: s,
        ops: appendCircuitOps(
          project,
          s,
          {
            label: T('Pompe à chaleur', 'Heat pump', 'Warmtepomp'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.heat-pump', props: { powerKw: 3.5 } }],
            cable: xvb(4),
          },
          originRcd(project, s),
        ),
      };
    },
  },
  {
    id: 'impulse-lighting',
    kind: 'control',
    name: { fr: 'Éclairage par télérupteur', en: 'Impulse-relay lighting', nl: 'Verlichting met teleruptor' },
    description: {
      fr: 'Trois boutons-poussoirs en parallèle commandent un télérupteur.',
      en: 'Three push-buttons in parallel drive an impulse relay.',
      nl: 'Drie parallelle drukknoppen sturen een teleruptor.',
    },
    ops: (project) => {
      const T = (f: string, e: string, n: string) => tr(project.meta.locale, f, e, n);
      const { sheetId, sheetOp } = controlSheet(
        project,
        T('Télérupteur éclairage', 'Impulse-relay lighting', 'Verlichting met teleruptor'),
      );
      const d = new Draw(sheetId);
      const [k] = freeTags(project, 'K', 1);
      const sTags = freeTags(project, 'S', 3);
      const [e] = freeTags(project, 'E', 1);
      d.wire(Q(20, 30), Q(160, 30), [], 'L');
      d.wire(Q(20, 140), Q(160, 140), [], 'N');
      const xs = [40, 65, 90];
      const pbs = xs.map((x, i) => d.el('iec.pushbutton-no', x - 5, 50, sTags[i]!));
      xs.forEach((x, i) => {
        d.wire(Q(x, 30), P(pbs[i]!, 'in'));
        d.wire(P(pbs[i]!, 'out'), Q(x, 80));
      });
      d.wire(Q(40, 80), Q(90, 80));
      const coil = d.el('iec.coil-impulse', 60, 92.5, k!);
      d.wire(Q(65, 80), P(coil, 'in'));
      d.wire(P(coil, 'out'), Q(65, 140));
      const contact = d.el('iec.contact-no', 125, 60, k!);
      const lamp = d.el('iec.signal-lamp', 125, 90, e!);
      d.wire(Q(130, 30), P(contact, 'in'));
      d.wire(P(contact, 'out'), P(lamp, 'in'));
      d.wire(P(lamp, 'out'), Q(130, 140));
      d.text(
        170,
        60,
        T(
          `${sTags.join(', ')} : boutons-poussoirs`,
          `${sTags.join(', ')}: push-buttons`,
          `${sTags.join(', ')}: drukknoppen`,
        ),
      );
      d.text(170, 67, T(`${k} : télérupteur`, `${k}: impulse relay`, `${k}: teleruptor`));
      return { sheetId, ops: [sheetOp, ...d.ops] };
    },
  },
  {
    id: 'shutters',
    kind: 'control',
    name: {
      fr: 'Volet roulant (montée / descente)',
      en: 'Roller shutter (up / down)',
      nl: 'Rolluik (op / neer)',
    },
    description: {
      fr: 'Deux boutons, deux contacteurs verrouillés entre eux, sortie vers le moteur.',
      en: 'Two buttons, two interlocked contactors, output to the motor.',
      nl: 'Twee knoppen, twee vergrendelde contactoren, uitgang naar de motor.',
    },
    ops: (project) => {
      const T = (f: string, e: string, n: string) => tr(project.meta.locale, f, e, n);
      const { sheetId, sheetOp } = controlSheet(project, T('Volet roulant', 'Roller shutter', 'Rolluik'));
      const d = new Draw(sheetId);
      const [ku, kd] = freeTags(project, 'K', 2);
      const [su, sd] = freeTags(project, 'S', 2);
      // X0/X1 are the incoming/outgoing strips of generated multi-line diagrams.
      const [x] = freeTags(project, 'X', 1, 2);
      d.wire(Q(20, 25), Q(250, 25), [], 'L');
      d.wire(Q(20, 160), Q(250, 160), [], 'N');
      // Control: Su → interlock NC of Kd → coil Ku ; Sd → NC of Ku → coil Kd.
      const col = (cx: number, s: string, interlock: string, coilTag: string) => {
        const pb = d.el('iec.pushbutton-no', cx - 5, 40, s);
        const nc = d.el('iec.contact-nc', cx - 5, 70, interlock);
        const coil = d.el('iec.coil', cx - 5, 100, coilTag);
        d.wire(Q(cx, 25), P(pb, 'in'));
        d.wire(P(pb, 'out'), P(nc, 'in'));
        d.wire(P(nc, 'out'), P(coil, 'in'));
        d.wire(P(coil, 'out'), Q(cx, 160));
      };
      col(50, su!, kd!, ku!);
      col(100, sd!, ku!, kd!);
      // Power: Ku and Kd main contacts to the motor terminals (up / down / N).
      const pc = (cx: number, tag: string, n: string) => {
        const k = d.el('iec.contactor', cx - 5, 50, tag);
        const t = d.el('iec.terminal', cx - 2.5, 100, x!, { number: n });
        d.wire(Q(cx, 25), P(k, 'in'));
        d.wire(P(k, 'out'), P(t, 'a'));
        return t;
      };
      pc(170, ku!, '1');
      pc(195, kd!, '2');
      const tn = d.el('iec.terminal', 217.5, 130, x!, { number: '3' });
      d.wire(P(tn, 'b'), Q(220, 160));
      d.text(
        165,
        115,
        T(
          `${x}:1 montée · ${x}:2 descente · ${x}:3 N → moteur du volet`,
          `${x}:1 up · ${x}:2 down · ${x}:3 N → shutter motor`,
          `${x}:1 op · ${x}:2 neer · ${x}:3 N → motor van het rolluik`,
        ),
        2.5,
      );
      d.text(
        20,
        178,
        T(
          `${su} : montée · ${sd} : descente — ${ku}/${kd} verrouillés électriquement`,
          `${su}: up · ${sd}: down — ${ku}/${kd} electrically interlocked`,
          `${su}: op · ${sd}: neer — ${ku}/${kd} elektrisch vergrendeld`,
        ),
        2.8,
      );
      return { sheetId, ops: [sheetOp, ...d.ops] };
    },
  },
];
